/**
 * One shape for route intelligence, whoever produced it.
 *
 * The reference tabs are shown in two quite different situations: while the
 * planner is open, where the data comes from live hooks and changes under the
 * operator's hands, and while a saved mission is being viewed or run, where it
 * comes off the mission and cannot change at all. Making the tabs aware of that
 * difference would mean two of every list, so instead both sources are adapted
 * to `RouteIntel` and the tabs only ever see the one shape.
 *
 * The fallback in `plannerIntel` is what actually fixes the complaint that
 * results disappear: a mission that has stored findings shows them immediately
 * on opening the planner, before any scan has run, and keeps showing them if a
 * scan is never run at all.
 */

import {
  DistanceUnits,
  RouteAssetsReport,
  RouteEventsReport,
  RouteLeg,
  RouteResult,
  RouteRiskReport,
  PlannerState,
} from '../types';
import { UseRouteAssets } from '../useRouteAssets';
import { UseRouteEvents } from '../useRouteEvents';
import { RouteWeatherReport, UseRouteWeather } from '../useRouteWeather';
import { UseRouteRisk } from '../useRouteRisk';

export interface IntelSection<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Describes a route that is no longer the current one. */
  stale: boolean;
  /** ISO timestamp of when this was produced. */
  capturedAt: string | null;
  /** Read back from the mission rather than fetched in this session. */
  persisted: boolean;
  /** Whether auto-refresh is armed. Absent for persisted-only sections. */
  enabled: boolean;
  setEnabled?: (on: boolean) => void;
  refresh?: () => void;
}

/** The route readout, flattened so it can be stored and replayed. */
export interface DirectionsIntel {
  distance: number;
  duration: number;
  durationTypical?: number;
  profile: string;
  departAt: string | null;
  /**
   * Units the operator planned in. Stored on the directions rather than left at
   * the `RouteIntel` level because this is the part that gets persisted, and a
   * vehicle reading the route back aloud has to use the same units the person
   * who planned it did.
   */
  units: DistanceUnits;
  legs: RouteLeg[];
  waypointLabels: string[];
  alternatives: Array<{
    id: string;
    distance: number;
    duration: number;
    summary: string;
    primary: boolean;
  }>;
}

export interface RouteIntel {
  units: DistanceUnits;
  directions: IntelSection<DirectionsIntel>;
  places: IntelSection<RouteAssetsReport>;
  events: IntelSection<RouteEventsReport>;
  weather: IntelSection<RouteWeatherReport>;
  corridor: IntelSection<RouteRiskReport>;
}

function section<T>(partial: Partial<IntelSection<T>>): IntelSection<T> {
  return {
    data: null,
    loading: false,
    error: null,
    stale: false,
    capturedAt: null,
    persisted: false,
    enabled: false,
    ...partial,
  };
}

/**
 * Turn the selected route into the directions view model.
 *
 * Step geometry is dropped. It is by far the largest part of a Mapbox response
 * and nothing in the panel draws from it — the map already has the full line,
 * and a step only needs its start point to be flown to.
 */
export function directionsFrom(
  state: PlannerState,
  route: RouteResult | null,
): DirectionsIntel | null {
  if (!route) return null;
  const primaryIndex = state.routes.indexOf(route);
  return {
    distance: route.distance,
    duration: route.duration,
    durationTypical: route.durationTypical,
    profile: state.options.profile,
    departAt: state.options.departAt,
    units: state.options.units,
    legs: route.legs.map((leg) => ({
      distance: leg.distance,
      duration: leg.duration,
      durationTypical: leg.durationTypical,
      summary: leg.summary,
      steps: leg.steps.map((step) => ({
        distance: step.distance,
        duration: step.duration,
        name: step.name,
        instruction: step.instruction,
        type: step.type,
        modifier: step.modifier,
        location: step.location,
        // Kept so the route can be spoken later. The panel does not read these,
        // but they are the only place the trigger distances live, and a route
        // saved without them can only be read aloud as a list of turns.
        voiceInstructions: step.voiceInstructions,
        bannerInstructions: step.bannerInstructions,
      })),
    })),
    waypointLabels: state.waypoints.map((w, i) => w.label || `Stop ${i + 1}`),
    alternatives: state.routes.map((alt, index) => ({
      id: alt.id,
      distance: alt.distance,
      duration: alt.duration,
      summary: alt.legs[0]?.summary || '',
      primary: index === (primaryIndex >= 0 ? primaryIndex : state.primaryIndex),
    })),
  };
}

/** Whatever a mission has stored, as sections. */
export interface StoredIntel {
  directions?: (DirectionsIntel & { captured_at?: string }) | null;
  places?: (RouteAssetsReport & { captured_at?: string }) | null;
  events?: (RouteEventsReport & { captured_at?: string }) | null;
  weather?: (RouteWeatherReport & { captured_at?: string }) | null;
  corridor?: (RouteRiskReport & { captured_at?: string }) | null;
}

/**
 * Intelligence for a saved mission: everything persisted, nothing live.
 *
 * `stale` comes from the server, which compares the stored signature against
 * the mission's current geometry. It is applied to every section at once
 * because they were all measured against the same line.
 */
/** Older saves omit lists. The Route tab calls `.find` on alternatives. */
export function normalizeDirections(
  value: DirectionsIntel | null | undefined,
): DirectionsIntel | null {
  if (!value || typeof value !== 'object') return null;
  return {
    ...value,
    legs: Array.isArray(value.legs) ? value.legs : [],
    waypointLabels: Array.isArray(value.waypointLabels) ? value.waypointLabels : [],
    alternatives: Array.isArray(value.alternatives) ? value.alternatives : [],
  };
}

export function missionIntel(
  stored: StoredIntel | null | undefined,
  options: { stale?: boolean; units?: DistanceUnits } = {},
): RouteIntel {
  const held = stored || {};
  const stale = !!options.stale;
  const build = <T,>(value: any): IntelSection<T> => section<T>({
    data: (value as T) || null,
    stale: stale && !!value,
    capturedAt: value?.captured_at || null,
    persisted: !!value,
  });
  return {
    units: options.units || 'mi',
    directions: build<DirectionsIntel>(normalizeDirections(held.directions)),
    places: build<RouteAssetsReport>(held.places),
    events: build<RouteEventsReport>(held.events),
    weather: build<RouteWeatherReport>(held.weather),
    corridor: build<RouteRiskReport>(held.corridor),
  };
}

/** The part of a scanning hook a tab actually consumes. */
export interface ScanHook<T> {
  report: T | null;
  loading: boolean;
  error: string | null;
  stale: boolean;
  capturedAt: string | null;
  enabled: boolean;
  setEnabled: (on: boolean) => void;
  refresh: () => void;
}

/** All three scanners, any of which may be absent. */
export interface ScanHooks {
  risk?: ScanHook<RouteRiskReport>;
  assets?: ScanHook<RouteAssetsReport>;
  events?: ScanHook<RouteEventsReport>;
  weather?: ScanHook<RouteWeatherReport>;
}

/**
 * A section backed by a scanner, falling back to what the mission has stored.
 *
 * Falling back per section rather than wholesale matters — an operator who
 * re-scans infrastructure on a changed route should see the new places beside
 * the events from last week, not lose the events for having refreshed
 * something else.
 *
 * The controls are attached either way. A held report with no way to replace it
 * is the complaint this dock was built to answer, and it applies just as much
 * to a mission opened without the planner as to one being edited.
 */
export function scanSection<T>(held: IntelSection<T>, hook?: ScanHook<T>): IntelSection<T> {
  const live = hook?.report ?? null;
  return section<T>({
    data: live || held.data,
    loading: !!hook?.loading,
    error: hook?.error ?? null,
    stale: live ? !!hook?.stale : held.stale,
    capturedAt: live ? hook?.capturedAt ?? null : held.capturedAt,
    persisted: live ? false : held.persisted,
    enabled: !!hook?.enabled,
    setEnabled: hook?.setEnabled,
    refresh: hook?.refresh,
  });
}

/**
 * Intelligence while the planner is open: live where we have it, stored where
 * we do not.
 */
export function plannerIntel(
  state: PlannerState,
  hooks: { risk?: UseRouteRisk; assets?: UseRouteAssets; events?: UseRouteEvents; weather?: UseRouteWeather },
  stored?: StoredIntel | null,
  storedStale?: boolean,
): RouteIntel {
  const fallback = missionIntel(stored, { stale: storedStale });
  const route = state.routes[state.primaryIndex] || null;
  const live = directionsFrom(state, route);

  return {
    units: state.options.units,
    directions: live
      ? section<DirectionsIntel>({
        data: live,
        loading: state.phase === 'routing',
        error: state.error?.message || null,
        enabled: true,
      })
      : section<DirectionsIntel>({
        ...fallback.directions,
        loading: state.phase === 'routing',
        error: state.error?.message || null,
        enabled: true,
      }),
    places: scanSection(fallback.places, hooks.assets),
    events: scanSection(fallback.events, hooks.events),
    weather: scanSection(fallback.weather, hooks.weather),
    corridor: scanSection(fallback.corridor, hooks.risk),
  };
}

/**
 * A saved mission's findings, with the scanners wired up so more can be pulled.
 *
 * The planner is where a scan usually happens, but forgetting to run one there
 * should not mean reopening the planner to fix it — the geometry is on the
 * mission, and every scan is a question about that geometry alone.
 */
export function scannedMissionIntel(base: RouteIntel, hooks: ScanHooks): RouteIntel {
  return {
    units: base.units,
    // Directions come off the route itself, not from a scan, so there is
    // nothing here to re-pull; the planner is the only thing that writes them.
    directions: base.directions,
    places: scanSection(base.places, hooks.assets),
    events: scanSection(base.events, hooks.events),
    weather: scanSection(base.weather, hooks.weather),
    corridor: scanSection(base.corridor, hooks.risk),
  };
}

/**
 * What to persist on the mission. Only sections that describe the route as it
 * stands now — storing a stale scan would mislabel it as current on reload.
 */
function directionsHaveSteps(data: DirectionsIntel | null | undefined): boolean {
  return !!data?.legs?.some((leg) => (leg.steps?.length ?? 0) > 0);
}

export function persistablePayload(intel: RouteIntel): StoredIntel {
  const out: StoredIntel = {};
  if (
    intel.directions.data
    && !intel.directions.stale
    && !intel.directions.persisted
    && directionsHaveSteps(intel.directions.data)
  ) {
    out.directions = intel.directions.data;
  }
  if (intel.places.data && !intel.places.stale && !intel.places.persisted) {
    out.places = intel.places.data;
  }
  if (intel.events.data && !intel.events.stale && !intel.events.persisted) {
    out.events = intel.events.data;
  }
  if (intel.weather.data && !intel.weather.stale && !intel.weather.persisted) {
    out.weather = intel.weather.data;
  }
  if (intel.corridor.data && !intel.corridor.stale && !intel.corridor.persisted) {
    out.corridor = intel.corridor.data;
  }
  return out;
}

/** Small numbers for the tab badges. Null means "nothing to count yet". */
export function tabCounts(intel: RouteIntel): {
  directions: number | null;
  places: number | null;
  events: number | null;
  weather: number | null;
  corridor: number | null;
} {
  const directions = intel.directions.data
    ? (intel.directions.data.legs || []).reduce(
      (sum, leg) => sum + (leg.steps?.length ?? 0),
      0,
    )
    : null;
  const places = intel.places.data
    ? intel.places.data.services.length + intel.places.data.constrictions.length
    : null;
  // Only events we are actually there for. Counting the ones we miss would put
  // a number on the tab that no amount of replanning can reduce.
  const events = intel.events.data
    ? intel.events.data.events.filter((e) => e.overlaps_transit !== false).length
    : null;
  const weather = intel.weather.data ? intel.weather.data.alerts.length : null;
  const corridor = intel.corridor.data ? intel.corridor.data.hotspots.length : null;
  return { directions, places, events, weather, corridor };
}
